import React, { useState, useMemo } from 'react';
import Chart from 'react-apexcharts';

/**
 * TrackerChartV2
 * ----------------------------------------------------------------------------
 * Polished ApexCharts dashboard card that replaces the v1 chart block.
 * Receives the same `chartData` object the v1 page already builds:
 *   { providers, totalEncounters, signedEncounters, unsignedEncounters,
 *     nonduplicatePatcount, CPTCount, UnitCount, maxvalue }
 * Legend entries are click-to-toggle (replacing the old checkbox row), and
 * Apex's built-in toolbar handles PNG/SVG/CSV export + print.
 */

const PALETTE = ['#1a9b8c', '#15803d', '#f59e0b', '#0e6b60', '#e8812a', '#64748b'];

const CHART_TYPES = [
  { value: 'bar', label: 'Column' },
  { value: 'horizontalBar', label: 'Horizontal Bar' },
  { value: 'line', label: 'Line' },
  { value: 'pie', label: 'Pie' },
  { value: 'donut', label: 'Donut' },
];

const TrackerChart = ({ chartData = {}, dateLabel = '' }) => {
  const [type, setType] = useState('horizontalBar');

  const {
    providers = [],
    totalEncounters = [],
    signedEncounters = [],
    unsignedEncounters = [],
    nonduplicatePatcount = [],
    CPTCount = [],
    UnitCount = [],
  } = chartData;

  const seriesDefs = useMemo(() => ([
    { name: 'Total Encounters', data: totalEncounters },
    { name: 'Signed', data: signedEncounters },
    { name: 'Unsigned', data: unsignedEncounters },
    { name: 'Total Patients', data: nonduplicatePatcount },
    { name: 'Total CPTs', data: CPTCount },
    { name: 'Total Units', data: UnitCount },
  ]), [totalEncounters, signedEncounters, unsignedEncounters, nonduplicatePatcount, CPTCount, UnitCount]);

  const sum = (arr) => arr.reduce((s, v) => s + (Number(v) || 0), 0);

  const isPie = type === 'pie' || type === 'donut';

  const options = useMemo(() => {
    const base = {
      colors: PALETTE,
      chart: {
        fontFamily: "'Segoe UI', Arial, sans-serif",
        toolbar: { show: true, tools: { download: true } },
        animations: { enabled: true, easing: 'easeinout', speed: 450 },
        zoom: { enabled: false },
      },
      legend: {
        position: 'top', horizontalAlign: 'center', fontSize: '13px', fontWeight: 600,
        markers: { radius: 12, width: 11, height: 11 }, itemMargin: { horizontal: 10, vertical: 4 },
      },
      dataLabels: { enabled: false },
      states: { hover: { filter: { type: 'lighten', value: 0.06 } } },
      tooltip: { theme: 'light', shared: true, intersect: false },
    };
    if (isPie) {
      return {
        ...base,
        labels: seriesDefs.map((s) => s.name),
        stroke: { width: 2, colors: ['#fff'] },
        dataLabels: { enabled: true, style: { fontSize: '12px', fontWeight: 700 }, dropShadow: { enabled: false } },
        plotOptions: { pie: { donut: { size: type === 'donut' ? '62%' : '0%' }, expandOnClick: true } },
        legend: { ...base.legend, position: 'right' },
      };
    }
    const horizontal = type === 'horizontalBar';
    return {
      ...base,
      grid: {
        borderColor: '#eef2f6', strokeDashArray: 4,
        xaxis: { lines: { show: horizontal } }, yaxis: { lines: { show: !horizontal } },
        padding: { left: 8, right: 12 },
      },
      xaxis: {
        categories: providers,
        labels: { style: { colors: '#64748b', fontSize: '12px' }, trim: true, maxHeight: 90 },
        axisBorder: { color: '#e2e8f0' }, axisTicks: { color: '#e2e8f0' },
      },
      yaxis: { labels: { style: { colors: '#64748b', fontSize: '12px' } } },
      plotOptions: {
        bar: {
          horizontal,
          borderRadius: 4,
          borderRadiusApplication: 'end',
          columnWidth: '62%',
          barHeight: '72%',
          dataLabels: { position: 'top' },
        },
      },
      fill: {
        type: type === 'line' ? 'solid' : 'gradient',
        gradient: { shade: 'light', type: horizontal ? 'horizontal' : 'vertical', shadeIntensity: 0.25, opacityFrom: 1, opacityTo: 0.88, stops: [0, 100] },
      },
      stroke: type === 'line' ? { curve: 'smooth', width: 3 } : { show: true, width: 1, colors: ['transparent'] },
      markers: type === 'line' ? { size: 4, strokeWidth: 2, hover: { size: 6 } } : { size: 0 },
    };
  }, [isPie, seriesDefs, providers, type]);

  const series = useMemo(() => {
    if (isPie) return seriesDefs.map((s) => sum(s.data));
    return seriesDefs;
  }, [isPie, seriesDefs]);

  const apexType = type === 'horizontalBar' ? 'bar' : type;

  // Dynamic height: give each provider room so horizontal bars aren't squished.
  const chartHeight = useMemo(() => {
    if (isPie) return 440;
    if (type === 'horizontalBar') return Math.max(420, providers.length * 42);
    return 460;
  }, [isPie, type, providers.length]);

  return (
    <div className="ptv2-chart-card">
      <div className="ptv2-chart-head">
        <div>
          <h3>Encounter Data Overview</h3>
          {dateLabel ? <p>{dateLabel}</p> : null}
        </div>
        <div className="ptv2-chart-types">
          {CHART_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              className={`ptv2-seg ${type === t.value ? 'active' : ''}`}
              onClick={() => setType(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="ptv2-chart-body">
        {providers.length === 0 ? (
          <div className="ptv2-mt-empty">No data to chart for this range.</div>
        ) : (
          <Chart options={options} series={series} type={apexType} height={chartHeight} />
        )}
      </div>
    </div>
  );
};

export default TrackerChart;
